import type { CrawlerProfileExtras } from "../types/experts/ShopexpertsCrawlerProfile";

/**
 * Trust & verification scoring model for crawled partner profiles.
 *
 * Structure from the profile-page design (Figma "Agency Profile", node
 * 363:12331): Trust score /60, Verification /40, Overall /100, "Missing
 * Signals" rows with "+N" point chips and a "Show signals breakdown
 * (achieved of total)" footer. The signal list and point values are the
 * official product criteria.
 */
export type CrawlerTrustSignalGroup = "trust" | "verification";

export interface CrawlerTrustSignalDefinition {
    key: string;
    label: string;
    points: number;
    group: CrawlerTrustSignalGroup;
}

/** Official scoring criteria (product-defined). */
export const CRAWLER_TRUST_SIGNALS: CrawlerTrustSignalDefinition[] = [
    // Trust signals — 60 max
    { key: "website_live", label: "Business website listed & live", points: 15, group: "trust" },
    { key: "shopify_directory_listing", label: "Shopify Directory listing", points: 15, group: "trust" },
    { key: "directory_reviews_25", label: "25+ Shopify Directory reviews", points: 10, group: "trust" },
    { key: "rating_4_8", label: "4.8+ average rating", points: 10, group: "trust" },
    { key: "years_in_business_5", label: "5+ years in business", points: 10, group: "trust" },
    // Verification signals — 40 max
    { key: "profile_claimed_verified", label: "Shopexperts profile claimed & verified", points: 10, group: "verification" },
    { key: "work_email_confirmed", label: "Work email confirmed", points: 5, group: "verification" },
    { key: "owner_linkedin_verified", label: "Owner LinkedIn verified", points: 5, group: "verification" },
    { key: "shopexperts_reviews_3", label: "3 Shopexperts reviews", points: 5, group: "verification" },
    { key: "case_study_published", label: "1 case study published", points: 5, group: "verification" },
    { key: "service_published", label: "1 service published", points: 5, group: "verification" },
    { key: "listing_recently_updated", label: "Listing updated in last 30 days", points: 5, group: "verification" },
];

const groupMax = (group: CrawlerTrustSignalGroup) =>
    CRAWLER_TRUST_SIGNALS.filter((s) => s.group === group).reduce((sum, s) => sum + s.points, 0);

export const CRAWLER_TRUST_MAX = groupMax("trust"); // 60
export const CRAWLER_VERIFICATION_MAX = groupMax("verification"); // 40
export const CRAWLER_OVERALL_MAX = CRAWLER_TRUST_MAX + CRAWLER_VERIFICATION_MAX; // 100

/**
 * Confidence tiers derived from the design examples:
 * overall 45 -> "Moderate Confidence", 75 and 92 -> "High Confidence".
 */
export function crawlerTrustConfidence(overall: number): NonNullable<NonNullable<CrawlerProfileExtras["trust"]>["confidence"]> {
    if (overall >= 70) return "high";
    if (overall >= 40) return "moderate";
    return "low";
}

/**
 * Computes the full trust block for the profile page from a set of achieved
 * signal keys. Consumers (dashboard mapper, fixtures, future API) only decide
 * which signals a profile has achieved; scores, missing signals and the
 * breakdown all derive from the shared model above.
 */
export function computeCrawlerTrust(achievedKeys: Iterable<string>): NonNullable<CrawlerProfileExtras["trust"]> {
    const achieved = new Set(achievedKeys);

    const sumFor = (group: CrawlerTrustSignalGroup) =>
        CRAWLER_TRUST_SIGNALS.filter((s) => s.group === group && achieved.has(s.key)).reduce((sum, s) => sum + s.points, 0);

    const trustScore = sumFor("trust");
    const verificationScore = sumFor("verification");
    const overall = trustScore + verificationScore;

    return {
        trust_score: { value: trustScore, max: CRAWLER_TRUST_MAX },
        verification_score: { value: verificationScore, max: CRAWLER_VERIFICATION_MAX },
        overall_score: { value: overall, max: CRAWLER_OVERALL_MAX },
        confidence: crawlerTrustConfidence(overall),
        missing_signals: CRAWLER_TRUST_SIGNALS
            .filter((s) => !achieved.has(s.key))
            .map((s) => ({ label: s.label, points: s.points })),
        signals: CRAWLER_TRUST_SIGNALS.map((s) => ({
            key: s.key,
            label: s.label,
            points: s.points,
            group: s.group,
            achieved: achieved.has(s.key),
        })),
    };
}
